/**
 * Media module — mock widget loaders.
 *
 * Every loader cuts the own-brand, chip-filtered rows to **coverage** (rows a
 * media outlet published) first — the prototype's `coverage()`, which each Media
 * card applied itself, to the comparison rows too.
 */
import type { WidgetLoaders } from '@/data-sources/contract';

import { aggregate } from '../engine/aggregate';
import { coverage } from '../engine/cuts';
import { OUTLET_LABELS, type MentionRow } from '../engine/generator';
import { metrics, type MetricId } from '../engine/metrics';
import { scoped } from '../query';
import { breakdownOf } from './mentions';
import { comparisonOf, timeSeriesOf } from './shared';

/** Coverage rows for this period and, when comparison is on, the previous one. */
export function mediaScope(query: Parameters<typeof scoped>[0]) {
  const s = scoped(query);
  return {
    ...s,
    stories: coverage(s.own),
    previousStories: s.ownPrevious ? coverage(s.ownPrevious) : null,
  };
}

function summary(metric: MetricId) {
  return async ({ query }: { query: Parameters<typeof scoped>[0] }) => {
    const { stories, previousStories } = mediaScope(query);
    return {
      figure: {
        current: metrics[metric].reduce(stories),
        previous: previousStories ? metrics[metric].reduce(previousStories) : null,
      },
      stories: stories.length,
    };
  };
}

export const mediaWidgetLoaders = {
  'media-total-mentions': summary('mentions'),
  'media-positive-mentions': summary('positiveMentions'),
  'media-negative-mentions': summary('negativeMentions'),

  'top-media-outlets': async ({ query }) => {
    const { stories } = mediaScope(query);
    return {
      outlets: breakdownOf(
        stories,
        (row: MentionRow) => row.outlet,
        (key) => ({ key, label: OUTLET_LABELS[key] ?? key }),
        'sentiment',
      ),
    };
  },

  'media-sentiment-over-time': async ({ query }) => {
    const { stories, previousStories, previousBounds, from, to } = mediaScope(query);
    const spec = { metric: 'mentions', from, to, splitBy: 'sentiment' } as const;
    const weekly = aggregate(stories, { ...spec, bucket: 'week' });
    const before = comparisonOf(previousStories, previousBounds, weekly, {
      metric: 'mentions',
      splitBy: 'sentiment',
    });
    return {
      weekly: timeSeriesOf(weekly, from, to, 'sentiment', before),
      daily: timeSeriesOf(aggregate(stories, { ...spec, bucket: 'day' }), from, to, 'sentiment'),
    };
  },
} satisfies Partial<WidgetLoaders>;
